// STEP 3-SHIP-B · Carrier selection modal · D-6C UX SIMPLIFICATION (2026-02-04)
// - 목표: 판매자가 "어떤 버튼을 눌러야 하나" 를 고민하지 않도록 primary CTA 를 1개로 통합.
// - CJ 선택 + Agent OK → [송장 준비 및 CJ 열기] 클릭 한 번으로:
//     Agent job 생성 · input.csv 저장 · CJ LOIS 오픈 · 파일 폴더 오픈까지 순차 실행.
// - Agent 미실행 · 미지원 carrier · 실패 케이스 → 명확한 안내 + 수동 fallback 만 노출.
// - LOCK 무변경: D-1~D-5A · D-6A · D-6B · Golden Input · watcher · parser · combined shipment
//   · CarrierProfiles · Backend · MyOrders 등 재개발 없음. 기존 함수를 orchestration 만.
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  X, Download, AlertCircle, Send, RefreshCw, Link as LinkIcon,
  ExternalLink, ChevronDown, ChevronUp, HelpCircle,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '@/lib/api';
import { CARRIER_PROFILES, buildCsvBlob, buildPhysicalShipments } from './carrierProfiles';
import {
  checkAgent, startPairing, confirmPairing, resetPairing, pushJob, getJob,
  computeRowsHash, shellOpenUrl, shellRevealJobFile, CJ_LOIS_FILE_RECEIPT_URL,
  startCjAutomation, getCjAutomationStatus, cancelCjAutomation,
} from '@/lib/agentBridge';

const CARRIER_ORDER = ['CJ', 'HANJIN', 'LOTTE', 'LOGEN', 'KOREA_POST', 'STANDARD_LIVEMOA'];

// D-6C · 실측된 검증 URL 만 자동 오픈 · 나머지는 파일 준비만. 추측 URL 금지 (§7).
const VERIFIED_OPEN_URLS = {
  CJ: CJ_LOIS_FILE_RECEIPT_URL,
};

export default function CarrierExportModal({ open, onClose, rows }) {
  const [carrierId, setCarrierId] = useState('CJ');
  const [busy, setBusy] = useState(false);
  const [agent, setAgent] = useState({ available: false, paired: false, loading: true });
  const [agentBusy, setAgentBusy] = useState(false);
  const [lastJobId, setLastJobId] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // D-6D-1 HOTFIX · CJ automation live status (poll-driven · null while
  // idle or on non-CJ carriers · never blocks D-6C fallback).
  const [cjAuto, setCjAuto] = useState(null);   // {jobId, state, reason} | null

  const probeAgent = async () => {
    setAgent((s) => ({ ...s, loading: true }));
    const h = await checkAgent();
    setAgent({ available: h.available, paired: !!h.paired, loading: false });
  };

  useEffect(() => { if (open) probeAgent(); }, [open]);

  // D-6D-1 HOTFIX · Poll CJ automation status while a session is live. Stops
  // on terminal / WAITING_USER_PRINT / NEEDS_USER_ACTION / VALIDATION_ERROR.
  // NEEDS_LOGIN keeps polling so the UI reflects the ATTACHING transition
  // the moment the seller finishes login/OTP. Modal close clears the timer
  // but never cancels the server-side session (§7 policy).
  useEffect(() => {
    if (!open) return undefined;
    if (!cjAuto?.jobId) return undefined;
    const TERMINAL = new Set([
      'CJ_WAITING_USER_PRINT', 'CJ_VALIDATION_ERROR', 'CJ_NEEDS_USER_ACTION',
      'CJ_FAILED', 'CJ_CANCELLED', 'CJ_COMPLETE',
    ]);
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await getCjAutomationStatus(cjAuto.jobId);
        if (cancelled || !s) return;
        setCjAuto((prev) => prev ? { ...prev, state: s.state, reason: s.reason } : prev);
        if (TERMINAL.has(s.state)) return;   // stop polling
        setTimeout(tick, 2500);
      } catch { /* transient · retry */ if (!cancelled) setTimeout(tick, 4000); }
    };
    setTimeout(tick, 800);
    return () => { cancelled = true; };
  }, [open, cjAuto?.jobId]);

  if (!open) return null;
  const profile = CARRIER_PROFILES[carrierId];
  const isReady = profile.status === 'available';
  const verifiedOpen = !!VERIFIED_OPEN_URLS[profile.id];
  const agentUsable = ['CJ', 'HANJIN', 'LOTTE'].includes(profile.id);   // 기존 Agent 지원 범위 (D-3 LOCK)

  // ── 파일 다운로드 (Manual fallback) ────────────────────────────────────
  // A4-2A · Fetch rack_location map from seller_inventory once, then inject
  //   per-item so `내품명` on CJ profile keeps 1:1 with the actual variant.
  const _fetchRackMap = async () => {
    try {
      const { data } = await api.get('/seller/inventory', { params: { limit: 20000 } });
      const map = {};
      for (const r of (data.items || [])) {
        const pid = r.product_id; const vk = r.variant_key || 'default';
        const rack = (r.rack_location || '').trim();
        if (pid && rack) map[`${pid}|${vk}`] = rack;
      }
      return map;
    } catch { return {}; }
  };
  const _canonVK = (opt) => {
    if (!opt || typeof opt !== 'object') return 'default';
    const keys = Object.keys(opt).filter((k) => k && opt[k] !== null && opt[k] !== '');
    if (!keys.length) return 'default';
    keys.sort();
    return keys.map((k) => `${k}=${opt[k]}`).join('|');
  };
  const _attachRacks = (shipments, rackMap) => {
    for (const ps of shipments) {
      for (const it of (ps.items || [])) {
        const vk = _canonVK(it.option);
        const key = `${it.productId || ''}|${vk}`;
        it.rackLocation = rackMap[key] || '';
      }
    }
  };
  const download = async () => {
    if (!isReady) { toast.error(`${profile.label} 양식은 아직 등록되지 않았습니다.`); return; }
    if (!rows?.length) { toast.error('내보낼 주문이 없습니다.'); return; }
    setBusy(true);
    try {
      const cids = Array.from(new Set(rows.map((o) => o.combined_shipment_id).filter(Boolean)));
      const combinedById = {};
      for (const cid of cids) {
        try { const r = await api.get(`/seller/combined-shipments/${cid}`); combinedById[cid] = r.data; }
        catch { /* row-only fallback */ }
      }
      const shipments = buildPhysicalShipments(rows, combinedById);
      const rackMap = await _fetchRackMap();
      _attachRacks(shipments, rackMap);
      const flat = [];
      shipments.forEach((ps) => profile.build(ps).forEach((r) => flat.push(r)));
      const ts = new Date().toISOString().slice(0, 10);
      const fname = `livemoa_${profile.id.toLowerCase()}_${ts}`;
      if (profile.fileExt === 'csv') {
        const blob = buildCsvBlob(profile.headers, flat);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `${fname}.csv`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        const ws = XLSX.utils.json_to_sheet(flat, { header: profile.headers });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, '출고');
        XLSX.writeFile(wb, `${fname}.xlsx`);
      }
      toast.success(`${profile.label} 파일 다운로드 · ${rows.length}건`);
    } catch (e) {
      toast.error(e?.message || '엑셀 생성 실패');
    } finally { setBusy(false); }
  };

  // ── Pairing / reset (unchanged) ────────────────────────────────────────
  const runPairing = async () => {
    setAgentBusy(true);
    try {
      const started = await startPairing();
      toast.message(`Agent 페어링 코드: ${started.pairing_code}`);
      await confirmPairing({ pairing_code: started.pairing_code, session_token: started.session_token });
      toast.success('Agent 페어링 완료');
      await probeAgent();
    } catch (e) { toast.error(e?.message || 'Agent 페어링 실패'); }
    finally { setAgentBusy(false); }
  };
  const runReset = async () => {
    setAgentBusy(true);
    try { await resetPairing(); toast.success('Agent 연결 해제'); await probeAgent(); }
    finally { setAgentBusy(false); }
  };

  // ── Agent 전송 (returns jobId · orchestration 재사용용) ────────────────
  const sendToAgentJob = async () => {
    if (!agentUsable) throw new Error('Agent 전송은 CJ · 한진 · 롯데 전용입니다. 파일 다운로드를 이용해주세요.');
    if (!rows?.length) throw new Error('내보낼 주문이 없습니다.');
    const cids = Array.from(new Set(rows.map((o) => o.combined_shipment_id).filter(Boolean)));
    const combinedById = {};
    for (const cid of cids) {
      try { const r = await api.get(`/seller/combined-shipments/${cid}`); combinedById[cid] = r.data; }
      catch { /* row-only fallback */ }
    }
    const shipments = buildPhysicalShipments(rows, combinedById);
    const carrierRows = [];
    shipments.forEach((ps) => profile.build(ps).forEach((r) => carrierRows.push(r)));
    const rows_hash = await computeRowsHash(profile.id, carrierRows);
    // D-3 idempotency: 동일 rows 는 rows_hash 로 dedup. jobId 는 새로 발급하지만 backend
    // 가 rows_hash 로 기존 job 을 반환하는 정책 그대로 REUSE.
    const jobId = `wbj_${profile.id.toLowerCase()}_${Date.now()}`;
    await pushJob({ job_id: jobId, carrier: profile.id, carrier_rows: carrierRows, rows_hash });
    const echoed = await getJob(jobId);
    if (echoed?.status !== 'FILE_READY') throw new Error('Agent job 상태가 FILE_READY 가 아닙니다.');
    setLastJobId(jobId);
    return { jobId, shipments: shipments.length, rowCount: carrierRows.length };
  };

  const runSendToAgentOnly = async () => {
    if (!agent.available) { toast.error('LIVEMOA 송장 Agent가 실행되지 않았습니다.'); return; }
    if (!agent.paired) { toast.error('먼저 Agent 페어링을 진행해주세요.'); return; }
    if (!isReady) { toast.error(`${profile.label} 양식은 아직 등록되지 않았습니다.`); return; }
    setAgentBusy(true);
    try {
      const r = await sendToAgentJob();
      toast.success(`송장 파일 준비 완료 · ${profile.label} · 실제 배송 ${r.shipments}건 · ${r.rowCount}행`);
    } catch (e) { toast.error(e?.message || 'Agent 전송 실패'); }
    finally { setAgentBusy(false); }
  };

  // ── D-6C PRIMARY CTA · 하나의 클릭으로 Agent job → 택배사 화면 오픈 ────
  const runPrimary = async () => {
    // Case 1 · Agent OFF → 안내만 (자동 EXE 실행 금지 · §2 · §5).
    if (!agent.available) { setHelpOpen(true); return; }
    // Case 2 · Agent ON but not paired → 페어링 UX 유도.
    if (!agent.paired) { return runPairing(); }
    // Case 3 · 양식 미등록.
    if (!isReady) { toast.error(`${profile.label} 양식은 아직 등록되지 않았습니다.`); return; }
    // Case 4 · Agent 미지원 carrier (표준양식 등) → 파일 다운로드 fallback.
    if (!agentUsable) { return download(); }

    setAgentBusy(true);
    try {
      // Step 1 · Agent 로 CJ input.csv 생성.
      const r = await sendToAgentJob();
      // D-6D-1 HOTFIX · CJ + verifiedOpen 경로 · 기존 D-6C helper 대신 실제
      // Playwright 자동화를 우선 시도. Agent 가 driver 를 붙일 수 있으면
      // persistent Edge 를 직접 열어 로그인/자동첨부/파일접수/접수확정/운송장출력
      // 화면 진입까지 진행. Chromium 번들/좌표 클릭 없음.
      if (profile.id === 'CJ' && verifiedOpen) {
        try {
          const started = await startCjAutomation(r.jobId, r.shipments);
          if (started?.driver_attached) {
            // 자동화 정상 시작 · 일반 브라우저(shellOpenUrl) 를 추가로 열지 않는다
            // (§2 · CJ 브라우저 2 개 방지). 폴더 reveal 도 필수 경로에서 제외.
            setCjAuto({ jobId: r.jobId, state: started.state,
                        reason: started.reason });
            toast.success('CJ 자동 송장 준비를 시작합니다.', {
              description: '전용 Edge 창에서 CJ 로그인/OTP 후 자동으로 다음 단계로 진행됩니다.',
            });
            return;
          }
          // driver 미부착 → D-6C fallback (§3 예외조건). 통상 dev/CI/Playwright 미설치.
          toast.message('Playwright 미설치 · 기존 파일 접수 도우미로 진행합니다.');
        } catch (e) {
          // start 자체가 실패 (401/404 등) → D-6C fallback.
          toast.message('CJ 자동화를 시작하지 못했습니다. 기존 방식으로 대체합니다.', {
            description: e?.message || 'unknown',
          });
        }
      }
      // D-6C fallback (기존 LOCK · non-CJ · Playwright 미설치 · start 실패 시).
      if (verifiedOpen) {
        try {
          await shellRevealJobFile(r.jobId);
          await shellOpenUrl(VERIFIED_OPEN_URLS[profile.id]);
          toast.success(`${profile.label} 접수 화면 · 파일 폴더가 열렸습니다.`, {
            description: '로그인 후 [찾기] → 이미 선택된 파일을 [열기] 하세요.',
          });
        } catch (e) {
          // job 은 보존 · 사용자가 secondary [파일 폴더 열기] 로 재시도 가능 (§13).
          toast.error(`파일은 준비되었으나 접수 화면을 열지 못했습니다: ${e?.message || 'unknown'}`);
        }
      } else {
        // Non-verified carrier (한진/롯데) → 파일 준비만 · URL 추측 금지 (§7).
        toast.success(`${profile.label} 송장 파일 준비 완료 · 실제 배송 ${r.shipments}건`, {
          description: '아래 [기타/수동 방법] 에서 파일 폴더를 열어 접수하세요.',
        });
      }
    } catch (e) {
      toast.error(e?.message || '송장 준비 실패 · 기존 CSV 다운로드를 이용해주세요.');
    } finally { setAgentBusy(false); }
  };

  const primaryLabel = (() => {
    if (!agent.available) return `Agent 실행 방법 안내`;
    if (!agent.paired)    return `Agent 페어링 진행`;
    if (!isReady)         return `${profile.label} · 양식 등록 필요`;
    if (!agentUsable)     return `${profile.label} 파일 다운로드`;
    if (profile.id === 'CJ') return `송장 준비 및 CJ 열기`;
    return `${profile.label} 송장 준비`;
  })();

  const primaryDisabled = agentBusy || busy || !isReady;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && onClose?.()}>
      <div className="bg-white rounded-lg max-w-sm w-full p-4 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="carrier-export-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-sm">택배사 선택</h3>
          <button onClick={onClose} disabled={busy} aria-label="닫기"><X size={16} /></button>
        </div>
        <p className="text-xs text-neutral-500">
          선택 주문 <b>{rows?.length || 0}건</b> · 합포 그룹은 자동으로 1개 배송으로 병합됩니다.
        </p>

        {/* ── Agent status strip ─────────────────────────────────────────── */}
        <div className="rounded border border-neutral-200 p-2 text-[11px] flex items-center justify-between"
             data-testid="agent-bridge-status">
          <div className="flex items-center gap-1.5">
            <LinkIcon size={11} className={agent.available ? 'text-emerald-600' : 'text-neutral-400'} />
            {agent.loading ? (
              <span className="text-neutral-500" data-testid="agent-state-loading">Agent 확인 중…</span>
            ) : !agent.available ? (
              <span className="text-neutral-500" data-testid="agent-state-offline">Agent 미실행</span>
            ) : !agent.paired ? (
              <span className="text-amber-700" data-testid="agent-state-unpaired">Agent 연결됨 · 페어링 필요</span>
            ) : (
              <span className="text-emerald-700" data-testid="agent-state-paired">Agent 연결됨 · 페어링 완료</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button onClick={probeAgent} disabled={agentBusy}
                    className="p-0.5 text-neutral-500 hover:text-black" data-testid="agent-refresh-btn"
                    aria-label="Agent 상태 새로고침">
              <RefreshCw size={11} />
            </button>
            {agent.available && !agent.paired && (
              <button onClick={runPairing} disabled={agentBusy}
                      className="px-2 py-0.5 rounded bg-amber-500 text-white text-[10px] font-bold"
                      data-testid="agent-pair-btn">페어링</button>
            )}
            {agent.available && agent.paired && (
              <button onClick={runReset} disabled={agentBusy}
                      className="px-2 py-0.5 rounded border text-[10px]"
                      data-testid="agent-reset-btn">연결 해제</button>
            )}
          </div>
        </div>

        {/* ── 택배사 선택 리스트 (fixed max-height + scroll · §6) ────────── */}
        <div className="space-y-1 max-h-[200px] overflow-y-auto pr-1" data-testid="carrier-list-scroll">
          {CARRIER_ORDER.map((id) => {
            const p = CARRIER_PROFILES[id];
            const disabled = p.status !== 'available';
            const hasVerified = !!VERIFIED_OPEN_URLS[id];
            return (
              <label key={id}
                     className={`flex items-center justify-between p-2 rounded border cursor-pointer text-sm ${
                       carrierId === id ? 'border-[#FF3B30] bg-red-50' : 'border-neutral-200'
                     } ${disabled ? 'opacity-60' : 'hover:bg-neutral-50'}`}
                     data-testid={`carrier-option-${id}`}>
                <div className="flex items-center gap-2">
                  <input type="radio" name="carrier" checked={carrierId === id}
                         onChange={() => !disabled && setCarrierId(id)}
                         disabled={disabled} className="accent-[#FF3B30]" />
                  <span className="font-semibold">{p.label}</span>
                  {p.fileExt && <span className="text-[10px] text-neutral-500 uppercase">.{p.fileExt}</span>}
                  {hasVerified && !disabled && (
                    <span className="text-[10px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold" data-testid={`carrier-verified-${id}`}>
                      자동 열기
                    </span>
                  )}
                </div>
                {disabled && (
                  <span className="text-[10px] text-amber-700 inline-flex items-center gap-0.5" data-testid={`carrier-unregistered-${id}`}>
                    <AlertCircle size={10} /> 양식 등록 필요
                  </span>
                )}
              </label>
            );
          })}
        </div>

        {/* ── Agent OFF 안내 (§5 · disabled UI 로 이유를 숨기지 않음) ────── */}
        {!agent.loading && !agent.available && (
          <div className="rounded border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 space-y-1.5" data-testid="agent-offline-panel">
            <p><b>LIVEMOA 송장 Agent</b>가 실행되어 있지 않습니다.</p>
            <p className="text-amber-800">Agent를 실행하면 CJ 접수 화면 자동 열기 · 결과 자동 감지가 활성화됩니다. 지금은 파일 다운로드로 수동 접수하실 수 있습니다.</p>
            <button onClick={() => setHelpOpen(true)}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-amber-500 text-amber-900 text-[10px] font-bold"
                    data-testid="agent-help-btn">
              <HelpCircle size={10}/> Agent 실행 방법
            </button>
          </div>
        )}

        {/* ── Non-verified carrier 안내 (§7 · 추측 URL 금지) ─────────────── */}
        {agent.available && agent.paired && isReady && agentUsable && !verifiedOpen && (
          <div className="rounded border border-neutral-300 bg-neutral-50 p-2 text-[11px] text-neutral-700" data-testid="carrier-unverified-notice">
            {profile.label}은 파일 준비까지만 자동화됩니다. 접수 화면은 아래 [기타 / 수동 방법]에서 파일 폴더를 확인 후 직접 열어주세요.
          </div>
        )}
        {agent.available && agent.paired && isReady && !agentUsable && (
          <div className="rounded border border-neutral-300 bg-neutral-50 p-2 text-[11px] text-neutral-700" data-testid="carrier-standard-notice">
            {profile.label}은 파일 다운로드 방식으로만 지원됩니다.
          </div>
        )}

        {/* ── PRIMARY CTA (§8 · single button) ───────────────────────────── */}
        <button onClick={runPrimary} disabled={primaryDisabled}
                className="w-full py-2.5 rounded-full bg-[#FF3B30] text-white text-sm font-black inline-flex items-center justify-center gap-1 disabled:opacity-50"
                data-testid="carrier-primary-cta">
          {agentBusy ? <RefreshCw size={13} className="animate-spin"/> : (verifiedOpen && agent.paired && agentUsable ? <ExternalLink size={13}/> : <Send size={13}/>)}
          {primaryLabel}
        </button>

        {/* D-6D-1 HOTFIX · CJ automation live status strip (compact · sanitized) */}
        {cjAuto?.jobId && (
          <div className="rounded border border-neutral-300 bg-neutral-50 p-2 text-[11px] space-y-1"
               data-testid="cj-auto-status">
            <div className="flex items-center justify-between">
              <span className="font-bold text-neutral-800">CJ 자동 송장 준비 중</span>
              <button onClick={async () => {
                try { await cancelCjAutomation(cjAuto.jobId); toast.message('CJ 자동화를 중단했습니다.'); }
                catch { /* server already terminal */ }
              }} className="text-[10px] text-neutral-500 hover:text-red-600"
                 data-testid="cj-auto-cancel-btn">중단</button>
            </div>
            <p className="text-neutral-600" data-testid="cj-auto-state">
              현재 상태: <b>{cjAuto.state || '준비 중'}</b>
            </p>
            {cjAuto.state === 'CJ_NEEDS_LOGIN' && (
              <p className="text-amber-800" data-testid="cj-auto-hint-login">
                CJ 로그인/OTP 를 완료해주세요. 완료 시 자동으로 다음 단계가 진행됩니다.
              </p>
            )}
            {cjAuto.state === 'CJ_ATTACHING' && (
              <p className="text-neutral-700" data-testid="cj-auto-hint-attach">파일을 자동 첨부하고 있습니다.</p>
            )}
            {cjAuto.state === 'CJ_VALIDATING' && (
              <p className="text-neutral-700" data-testid="cj-auto-hint-validating">CJ 파일접수 결과를 확인하고 있습니다.</p>
            )}
            {cjAuto.state === 'CJ_VALIDATION_ERROR' && (
              <p className="text-red-700" data-testid="cj-auto-hint-verror">
                검증 실패 · {cjAuto.reason || '오류 건수를 확인해주세요.'}
              </p>
            )}
            {cjAuto.state === 'CJ_NEEDS_USER_ACTION' && (
              <p className="text-amber-800" data-testid="cj-auto-hint-need-user">
                자동 진행 중단 · {cjAuto.reason || '판매자 확인이 필요합니다.'}
              </p>
            )}
            {cjAuto.state === 'CJ_WAITING_USER_PRINT' && (
              <p className="text-emerald-800" data-testid="cj-auto-hint-print">
                송장 준비 완료 · CJ 화면 오른쪽 위 [출력] 을 눌러주세요.
              </p>
            )}
            {cjAuto.state === 'CJ_FAILED' && (
              <p className="text-red-700" data-testid="cj-auto-hint-failed">
                실패 · {cjAuto.reason || 'CJ 화면 상태를 확인해주세요.'}
              </p>
            )}
          </div>
        )}

        {/* ── 기타 / 수동 방법 (§9 · secondary collapsible) ──────────────── */}
        <div className="border-t border-neutral-100 pt-2" data-testid="carrier-secondary">
          <button onClick={() => setManualOpen((v) => !v)}
                  className="w-full flex items-center justify-between text-[11px] text-neutral-500 hover:text-neutral-800"
                  data-testid="carrier-secondary-toggle">
            <span>기타 / 수동 방법</span>
            {manualOpen ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
          </button>
          {manualOpen && (
            <div className="mt-2 space-y-1.5" data-testid="carrier-secondary-panel">
              {agent.available && agent.paired && isReady && agentUsable && (
                <button onClick={runSendToAgentOnly} disabled={agentBusy || busy}
                        className="w-full px-2 py-1.5 rounded border border-emerald-500 text-emerald-700 text-[11px] font-bold inline-flex items-center justify-center gap-1 disabled:opacity-40"
                        data-testid="carrier-export-send-agent-btn">
                  <Send size={11}/> Agent 로만 자동 전송 (화면 열기 없음)
                </button>
              )}
              <button onClick={download} disabled={busy || !isReady}
                      className="w-full px-2 py-1.5 rounded border border-neutral-300 text-neutral-800 text-[11px] font-bold inline-flex items-center justify-center gap-1 disabled:opacity-40"
                      data-testid="carrier-export-download-btn">
                <Download size={11}/> 파일 다운로드
              </button>
              {agent.available && agent.paired && lastJobId && (
                <button onClick={async () => {
                          try { await shellRevealJobFile(lastJobId); toast.success('파일 폴더가 열렸습니다.'); }
                          catch (e) { toast.error(e?.message || '폴더를 열지 못했습니다.'); }
                        }}
                        className="w-full px-2 py-1.5 rounded border border-neutral-300 text-neutral-800 text-[11px] font-bold inline-flex items-center justify-center gap-1"
                        data-testid="cj-reveal-file-btn">
                  <ExternalLink size={11}/> 파일 폴더 열기
                </button>
              )}
              {agent.available && agent.paired && lastJobId && verifiedOpen && (
                <button onClick={async () => {
                          try {
                            await shellRevealJobFile(lastJobId);
                            await shellOpenUrl(VERIFIED_OPEN_URLS[profile.id]);
                            toast.success('CJ 접수 화면 · 파일 폴더가 열렸습니다.');
                          } catch (e) { toast.error(e?.message || 'CJ 접수 화면을 열지 못했습니다.'); }
                        }}
                        className="w-full px-2 py-1.5 rounded border border-emerald-500 text-emerald-700 text-[11px] font-bold inline-flex items-center justify-center gap-1"
                        data-testid="cj-open-receipt-btn">
                  <ExternalLink size={11}/> CJ 접수 화면 다시 열기
                </button>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-1">
          <button onClick={onClose} disabled={busy}
                  className="px-3 py-1.5 rounded border text-xs"
                  data-testid="carrier-cancel-btn">닫기</button>
        </div>
      </div>

      {/* ── Agent 실행 방법 안내 다이얼로그 (§5 · 웹앱이 EXE 자동실행 안 함) ── */}
      {helpOpen && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={() => setHelpOpen(false)}>
          <div className="bg-white rounded-lg max-w-sm w-full p-4 space-y-2" onClick={(e) => e.stopPropagation()} data-testid="agent-help-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-bold text-sm">LIVEMOA 송장 Agent 실행 방법</h3>
              <button onClick={() => setHelpOpen(false)} aria-label="닫기"><X size={16}/></button>
            </div>
            <ol className="text-xs text-neutral-700 list-decimal pl-4 space-y-1">
              <li>바탕화면 또는 시작 메뉴의 <b>LIVEMOA Waybill Agent</b>를 실행합니다 (트레이 창이 표시됩니다).</li>
              <li>바로가기가 없다면 Agent 트레이 창의 <b>[바탕화면 shortcut 만들기]</b> / <b>[시작 메뉴 shortcut 만들기]</b> 로 한 번만 생성해두세요.</li>
              <li>브라우저로 돌아와 [상태 새로고침] 을 눌러 연결을 확인합니다.</li>
              <li>[페어링] 버튼으로 페어링 코드를 확인 후 승인합니다.</li>
              <li>다음부터는 <b>[Windows 시작 시 자동 실행]</b> 옵션을 켜두면 매번 실행할 필요가 없습니다.</li>
            </ol>
            <p className="text-[11px] text-neutral-500">
              보안상 웹 페이지가 PC의 Agent 프로그램을 자동으로 실행하지는 않습니다. Agent EXE가 없다면 판매자 지원 채널을 통해 최신 빌드를 요청해주세요.
            </p>
            <div className="flex justify-end">
              <button onClick={() => setHelpOpen(false)}
                      className="px-3 py-1.5 rounded bg-neutral-900 text-white text-xs font-bold"
                      data-testid="agent-help-close-btn">확인</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
